import React, { useContext, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { ProfileContext } from "../context/ProfileContext";
import { fx_FB_RedeemInviteCode, REDEEM_RESULT } from "../functions/Fx_InviteCodes";

interface RedeemInviteCodeModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
}

const RedeemInviteCodeModal: React.FC<RedeemInviteCodeModalProps> = ({ isOpen, onDidDismiss }) => {
  const { profile, buttonColor } = useContext(ProfileContext) as any;
  const [presentToast] = useIonToast();
  const [inviteCodeInput, setInviteCodeInput] = useState<string>("");
  const [redeeming, setRedeeming] = useState<boolean>(false);

  const handleClose = () => {
    setInviteCodeInput("");
    onDidDismiss();
  };

  const handleRedeem = async () => {
    const code = inviteCodeInput.trim();
    if (!code) {
      presentToast({ message: "Enter an invite code first.", duration: 2500, position: "top", color: "warning" });
      return;
    }
    if (!profile?.UserId) return;
    setRedeeming(true);
    try {
      const outcome = await fx_FB_RedeemInviteCode(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company || "",
        profile.ProfilePicURL || "../assets/profilePic.png",
        code
      );
      if (outcome.result === REDEEM_RESULT.Success) {
        presentToast({
          message: `You've joined ${outcome.schedulerFirstName ?? ""} ${outcome.schedulerLastName ?? ""}'s group.`.replace(/\s+/g, " ").trim(),
          duration: 3000,
          position: "top",
          color: "success",
        });
        handleClose();
      } else if (outcome.result === REDEEM_RESULT.AlreadyAssociate) {
        presentToast({
          message: `You're already in ${outcome.schedulerFirstName ?? ""} ${outcome.schedulerLastName ?? ""}'s group.`.replace(/\s+/g, " ").trim(),
          duration: 2500,
          position: "top",
          color: "medium",
        });
        handleClose();
      } else if (outcome.result === REDEEM_RESULT.SelfCode) {
        presentToast({ message: "You can't redeem your own invite code.", duration: 2500, position: "top", color: "warning" });
      } else {
        presentToast({ message: "Invite code not recognized.", duration: 2500, position: "top", color: "warning" });
      }
    } catch (err) {
      presentToast({ message: "Something went wrong. Please try again.", duration: 2500, position: "top", color: "danger" });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      breakpoints={[0, 0.5, 0.85]}
      initialBreakpoint={0.5}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Input Invite Code</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose} aria-label="Close">
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <p style={{ marginTop: 0 }}>
          Enter the invite code your Scheduler sent you. You'll be added to their group automatically.
        </p>
        <IonItem>
          <IonLabel position="stacked">Invite Code</IonLabel>
          <IonInput
            autoCapitalize="characters"
            type="text"
            name="InviteCode"
            placeholder="e.g. JD1234"
            value={inviteCodeInput}
            onIonChange={(e: any) => setInviteCodeInput((e.target.value ?? "").toString().toUpperCase())}
            clearInput
          />
        </IonItem>
        <IonButton
          expand="block"
          color={buttonColor}
          onClick={handleRedeem}
          disabled={redeeming || !inviteCodeInput.trim()}
          style={{ marginTop: 16 }}
        >
          {redeeming ? "Joining\u2026" : "Join Group"}
        </IonButton>
      </IonContent>
    </IonModal>
  );
};

export default RedeemInviteCodeModal;
